import React from 'react';
import { Panel as SharedPanel } from '@cloudsam/ui';
import PanelHeaderActions from './PanelHeaderActions';

const PANEL_ICONS = {
    executed: (
        <>
            <rect x="3" y="2" width="12" height="14" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
            <line x1="6" y1="6" x2="12" y2="6" stroke="currentColor" strokeWidth="1.2" opacity="0.5" />
            <line x1="6" y1="9" x2="11" y2="9" stroke="currentColor" strokeWidth="1.2" opacity="0.4" />
            <polyline points="3,12 5.5,14.5 9,10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </>
    ),
    users: (
        <>
            <circle cx="6.5" cy="5.5" r="2.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
            <path d="M1 14.5c0-3 2.5-4.5 5.5-4.5s5.5 1.5 5.5 4.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <circle cx="12" cy="5" r="2" fill="none" stroke="currentColor" strokeWidth="1.2" opacity="0.5" />
            <path d="M15.5 14c0-2.2-1.8-3.5-3.5-3.8" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity="0.5" />
        </>
    ),
    analytics: (
        <>
            <rect x="1.5" y="10" width="3" height="5" rx="0.5" fill="currentColor" opacity="0.8" />
            <rect x="6.5" y="6" width="3" height="9" rx="0.5" fill="currentColor" opacity="0.55" />
            <rect x="11.5" y="3" width="3" height="12" rx="0.5" fill="currentColor" opacity="0.35" />
            <polyline points="2,9 5.5,5 10,7 14.5,2" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.7" />
        </>
    ),
    ready: (
        <>
            <path d="M8 1L8 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <path d="M4.5 5L8 1L11.5 5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M3 8v4.5c0 1.1.9 2 2 2h6c1.1 0 2-.9 2-2V8" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </>
    ),
    // Staged is Ready's mirror: the arrow points down into the tray, because
    // this is where recipients are put, not where surveys leave from.
    staged: (
        <>
            <path d="M8 1L8 9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <path d="M4.5 6L8 9.5L11.5 6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M3 8v4.5c0 1.1.9 2 2 2h6c1.1 0 2-.9 2-2V8" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </>
    ),
    deployed: (
        <>
            <path d="M2 10c0-3.3 2.7-6 6-6 2.8 0 5.2 2 5.8 4.6.1 0 .2 0 .2 0 1.7 0 3 1.3 3 3s-1.3 3-3 3H3c-1.7 0-3-1.3-3-3 0-1.4 1-2.6 2.3-2.9" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
            <polyline points="6,12 8,14.5 12,10" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </>
    ),
};

// Inherits the panel-header ink from the shared stylesheet so the icon follows
// the surface tint rather than a fixed grey.
const PanelIcon = ({ name }) => {
    const paths = PANEL_ICONS[name];
    if (!paths) return null;
    return (
        <svg width={16} height={16} viewBox="0 0 16 16" style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}>
            {paths}
        </svg>
    );
};

/**
 * Console's Panel is now a thin adapter over the shared <Panel>
 * (surface-identity-and-shell.md §3). It keeps Console's existing call
 * signature — `icon` as a name string, `actions` as an array handed to
 * PanelHeaderActions — so the nine call sites are unchanged, while the frame,
 * header strip and title colour come from the shared component and follow the
 * surface tint.
 *
 * `count` is new and optional: the pill text ("0 staged", "20 matching").
 *
 * `controls` is passed straight through to the shared Panel. It is the view
 * group that sits beside the title — Staged's channel selector, for one — as
 * opposed to `actions`, which are the commands on the right.
 */
const Panel = ({ title, icon, count, controls, children, style, bodyStyle, actions, selection, actionState }) => (
    <SharedPanel
        title={title}
        count={count}
        controls={controls}
        icon={icon ? <PanelIcon name={icon} /> : null}
        actions={actions && actions.length > 0
            ? <PanelHeaderActions actions={actions} selection={selection} state={actionState} />
            : null}
        style={style}
        bodyStyle={{ padding: 12, ...bodyStyle }}
    >
        {children}
    </SharedPanel>
);

export default Panel;
